import type { StaffPermission, StaffPrincipal, StaffRole } from "./types.ts";

// Permission bundles per role, spec-locked to the matrix in
// `docs/requirements/08-role-permission.md` §3 (FR-ROLE-005). Notably:
//   - "accept/reject order" (order:accept) is granted to waiter/bartender/
//     floor_manager, NOT runner (runner is fulfilment-only, note 1) and NOT
//     cashier (money and order-acceptance are separate concerns).
//   - "update order status" (order:fulfil, preparing/ready/served) additionally
//     includes runner (note 2 — runners mark items delivered).
//   - "cancel order" (order:cancel) is waiter/bartender/cashier/floor_manager,
//     NOT runner and NOT moderator.
//   - "confirm gift fulfilment" (gift:fulfil/gift:reject = fulfilled/
//     staff_rejected) is waiter/bartender/floor_manager, NOT runner (note 3 —
//     distinct from the customer's own recipient accept/reject) and NOT cashier.
//   - money actions (payment:capture/refund:request) and moderation actions
//     (moderation:action, which also gates ban/unban) are never combined on a
//     single default role (FR-ROLE-006 separation of duties).
//   - emergency-disable-social (social:disable) is floor_manager only — NOT
//     moderator (note 7, high blast radius restricted to floor-manager/owner).
export const rolePermissions: Record<StaffRole, StaffPermission[]> = {
  waiter: [
    "table:view",
    "table:manage",
    "order:view",
    "order:accept",
    "order:fulfil",
    "order:cancel",
    "gift:view",
    "gift:fulfil",
    "gift:reject",
  ],
  runner: ["table:view", "order:view", "order:fulfil", "gift:view"],
  bartender: [
    "order:view",
    "order:accept",
    "order:fulfil",
    "order:cancel",
    "gift:view",
    "gift:fulfil",
    "gift:reject",
  ],
  cashier: [
    "order:view",
    "order:cancel",
    "cashier:view",
    "payment:capture",
    "refund:request",
    "screen:view",
    "screen:action",
  ],
  moderator: [
    "moderation:view",
    "moderation:action",
    "incident:view",
    "incident:create",
    "screen:view",
    "screen:action",
  ],
  floor_manager: [
    "session:manage",
    "social:disable",
    "table:view",
    "table:manage",
    "order:view",
    "order:accept",
    "order:fulfil",
    "order:cancel",
    "gift:view",
    "gift:fulfil",
    "gift:reject",
    "moderation:view",
    "moderation:action",
    "incident:view",
    "incident:create",
    "cashier:view",
    "payment:capture",
    "refund:request",
    "screen:view",
    "screen:action",
  ],
};

export function getEffectivePermissions(roles: StaffRole[]): StaffPermission[] {
  return Array.from(new Set(roles.flatMap((role) => rolePermissions[role]))).sort();
}

/** UI gating only; real authorization must be enforced by the server. */
export function hasPermission(
  principal: Pick<StaffPrincipal, "permissions"> | null | undefined,
  permission: StaffPermission,
): boolean {
  return Boolean(principal?.permissions.includes(permission));
}

export function buildPrincipal(input: Omit<StaffPrincipal, "permissions">): StaffPrincipal {
  return { ...input, permissions: getEffectivePermissions(input.roles) };
}
